import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { IOption, Select } from "@digital-services/ui";

import { OrganizationRole } from "@organization/models/OrganizationRole";
import { isNotEmpty } from "@validators/NotEmptyValidator";

import { useInput } from "@hooks/useInput";
import { OnInputChangeFunctor } from "@utils/validators/models/ValidationResult";

interface Props {
  label: string;
  selectedRole: string | undefined;
  disabled?: boolean;
  onRoleChange: OnInputChangeFunctor;
}

export const OrganizationRoleDropDown = (props: Props) => {
  const { label, selectedRole, disabled, onRoleChange } = props;

  const { t } = useTranslation(undefined, {
    keyPrefix: "organization.invite.users"
  });

  const options = useMemo(() => {
    return Object.values(OrganizationRole).map(roleName => {
      return { label: roleName, value: roleName };
    });
  }, []);

  const [selectedOption, setSelectedOption] = useState<IOption | null>(null);

  useEffect(() => {
    if (selectedRole) {
      const foundOption = options.find(
        type => type.value.toString() === selectedRole
      );
      setSelectedOption(foundOption ?? null);
    }
  }, [options, selectedRole]);

  const { isValid, errorMessage, valueChangeHandler } = useInput(
    [isNotEmpty(() => t("validation.string.not-empty", { fieldName: label }))],
    selectedRole
  );

  const handleChange = useCallback(
    (option: IOption) => {
      setSelectedOption(option);
      const { value, isValid } = valueChangeHandler(option.value.toString());
      if (onRoleChange) onRoleChange(value, isValid);
    },
    [valueChangeHandler, onRoleChange]
  );

  return (
    <Select
      data-testid="organization-role"
      disabled={disabled}
      label={label}
      error={!isValid}
      helperText={isValid ? undefined : errorMessage}
      options={options}
      selected={selectedOption}
      onChange={handleChange}
    />
  );
};
